import { useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Sparkle, Receipt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import api from "../lib/api";

/**
 * Universal "Auto-Fetch from Vendor Bill" upload modal.
 *
 * Usage:
 *   <PurchaseAutoFetchModal
 *     open={...} onOpenChange={...}
 *     companyId={activeId}
 *     target="expense" | "voucher"
 *     onParsed={(data) => { ... apply data.expense_prefill or data.voucher_prefill ... }}
 *   />
 */
export default function PurchaseAutoFetchModal({ open, onOpenChange, companyId, target, onParsed }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = () => { if (!busy) { setFile(null); onOpenChange(false); } };

  const parse = async () => {
    if (!companyId) { toast.error("Pick a company first"); return; }
    if (!file) { toast.error("Choose a PDF first"); return; }
    if (!/\.pdf$/i.test(file.name)) { toast.error("Only PDF supported"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("company_id", companyId);
      const { data } = await api.post("/finance/purchase/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const vendorMsg = data?.vendor?.created_new
        ? `Vendor «${data.vendor.name}» auto-created in Clients & Vendors`
        : `Matched existing vendor «${data.vendor.name}»`;
      toast.success(`${vendorMsg} · ${data?._meta?.item_count || 0} item(s) parsed`);
      onParsed && onParsed(data);
      setFile(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Sparkle size={18} weight="bold" className="text-[#D90429]"/> Auto-Fetch from Vendor Bill
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#4A4D57] leading-relaxed">
            Upload a PDF of a <b>purchase bill / vendor invoice</b>. Our AI extracts the vendor, bill no., date, line items
            and totals — then pre-fills a new <b>{target === "voucher" ? "Voucher" : "Operating Expense"}</b>.
            If the vendor doesn&apos;t exist yet, we&apos;ll add them to <b>Finance → Clients & Vendors</b> automatically.
          </p>
          <label
            htmlFor="purchase-fetch-input"
            className="block border-2 border-dashed border-black/15 hover:border-[#D90429] p-6 text-center cursor-pointer transition-colors"
            data-testid="purchase-drop-zone"
          >
            <UploadSimple size={28} weight="bold" className="mx-auto mb-2 text-[#717680]"/>
            <div className="text-sm font-semibold text-[#0A0A0A]">{file ? file.name : "Click to choose a PDF"}</div>
            <div className="text-[11px] text-[#717680] mt-1 font-mono-shelv uppercase tracking-widest">
              PDF only · Multi-page supported
            </div>
            <input
              id="purchase-fetch-input"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="purchase-file-input"
            />
          </label>
          {busy && (
            <div className="flex items-center gap-2 text-xs text-[#0D9488] font-semibold">
              <span className="inline-block w-3 h-3 border-2 border-[#0D9488] border-t-transparent rounded-full animate-spin"/>
              Reading bill with Gemini Vision · upserting vendor · this may take 10–30 seconds
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={close} disabled={busy} className="px-4 py-2 border border-black/15 text-sm disabled:opacity-50">Cancel</button>
          <button
            onClick={parse}
            disabled={!file || busy}
            className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="purchase-extract-submit"
          >
            <Sparkle size={14} weight="bold"/> {busy ? "Parsing…" : "Parse & Pre-fill"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
